import { useState } from 'react';
import {
  Modal,
  ModalContent,
  Button,
  Control,
  Label,
  Select,
  SelectOption,
  LockIcon,
} from '@forge/common';
import { useApiMutation } from '@/hooks/useApiMutation';
import { partnerKeysApi } from '@/api/partnerKeys';

interface GenerateHoneywellKeyDialogProps {
  partnerId: string;
  partnerName: string;
  hasExistingKey: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Generates the Honeywell (our) side of a partner PGP keyring in-platform.
 * The private key is created and stored in Vault by sftp-service and never
 * reaches the browser — this dialog only triggers generation and shows the
 * resulting public key row once the keyring reloads.
 */
const GenerateHoneywellKeyDialog = ({
  partnerId,
  partnerName,
  hasExistingKey,
  onClose,
  onSuccess,
}: GenerateHoneywellKeyDialogProps) => {
  const [rsaBits, setRsaBits] = useState('4096');

  const { mutate: generate, isSubmitting } = useApiMutation<void, void>({
    mutationFn: async () => {
      await partnerKeysApi.generateHoneywell(partnerId, { rsaBits: Number(rsaBits) });
    },
    successMessage: 'Honeywell keypair generated',
    errorMessage: 'Failed to generate Honeywell keypair',
    onSuccess: () => onSuccess(),
  });

  return (
    <Modal
      title="Generate Honeywell keypair"
      onModalClose={onClose}
      onBackdropClick={onClose}
      footerLeft={
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
      }
      footerRight={
        <Button
          variant="primary"
          icon={<LockIcon />}
          onClick={() => generate()}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Generating...' : 'Generate keypair'}
        </Button>
      }
    >
      <ModalContent padded>
        <div className="flex flex-col gap-3">
          <p className="text-sm">
            This generates a new PGP keypair for <strong>{partnerName}</strong>. The private key
            is stored securely in Vault and used to decrypt files partners send us; only the public
            key is recorded here for you to hand to the partner.
          </p>

          <Control hideSpacing="bottom">
            <Label htmlFor="gen-rsa-bits">Key size</Label>
            <Select id="gen-rsa-bits" value={rsaBits} onChange={(e) => setRsaBits(e.target.value)}>
              <SelectOption value="4096" label="RSA 4096 (recommended)">
                RSA 4096 (recommended)
              </SelectOption>
              <SelectOption value="3072" label="RSA 3072">
                RSA 3072
              </SelectOption>
              <SelectOption value="2048" label="RSA 2048">
                RSA 2048
              </SelectOption>
            </Select>
          </Control>

          {hasExistingKey && (
            <p className="text-xs text-color-error">
              An active Honeywell keypair already exists. Generating a new one deactivates the
              previous key — files still encrypted to the old key will no longer be decryptable
              until senders switch to the new public key.
            </p>
          )}

          <p className="text-xs text-color-tertiary">
            The private key never leaves the server. Generation may take a few seconds for RSA 4096.
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default GenerateHoneywellKeyDialog;
